import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { useGetReviewActivity } from "../../api/queries/review.queries";
import { formatRelativeTimeUTC } from "@/shared/utils/date/format-relative-time";
import {
  ArrowRight,
  History,
  AlertTriangle,
  ChevronDown,
  Images,
  Minus,
  Plus,
  RotateCcw,
  Users,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/components/ui/popover";
import {
  BUCKET_LABEL_KEYS,
  buildSessions,
  summarizeChips,
  type ActivityChip,
  type ReviewSession,
} from "./recent-reviews.utils";

interface RecentReviewsProps {
  onImageClick?: (imageId: number) => void;
}

/* One chip per review action — visual per action kind:
 *   corrected  ~~model~~ → human (emerald)
 *   added      + human (violet)
 *   removed    ~~label~~ (red)
 *   undo       model restored (muted)                                 */
const ChipContent = ({
  chip,
  t,
}: {
  chip: ActivityChip;
  t: TFunction<"mlExports">;
}) => {
  switch (chip.kind) {
    case "corrected":
      return (
        <>
          <span className="text-muted-foreground line-through opacity-50">
            {chip.model_label}
          </span>
          <ArrowRight className="h-2 w-2 text-muted-foreground/40" />
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
            {chip.human_label}
          </span>
        </>
      );
    case "added":
      return (
        <span className="inline-flex items-center gap-0.5 font-semibold text-violet-600 dark:text-violet-400">
          <Plus className="h-2.5 w-2.5" />
          {chip.human_label}
        </span>
      );
    case "removed":
      return (
        <span className="inline-flex items-center gap-0.5 font-semibold text-red-600 dark:text-red-400">
          <Minus className="h-2.5 w-2.5" />
          <span className="line-through">
            {chip.human_label ?? chip.model_label ?? chip.family_key}
          </span>
        </span>
      );
    case "undo":
      return (
        <span className="inline-flex items-center gap-0.5 text-muted-foreground">
          <RotateCcw className="h-2.5 w-2.5 opacity-60" />
          {chip.model_label}
          <span className="opacity-60">{t("recent.restored")}</span>
        </span>
      );
  }
};

interface SessionRowProps {
  session: ReviewSession;
  isExpanded: boolean;
  onToggle: (sessionId: string) => void;
  onImageClick?: (imageId: number) => void;
  t: TFunction<"mlExports">;
}

/* Memoized so toggling one session's expanded state doesn't re-render the
 * entire list — only the row whose `isExpanded` actually flips. */
const SessionRow = memo(
  ({ session, isExpanded, onToggle, onImageClick, t }: SessionRowProps) => {
    const isBatch = session.images.length > 1;
    const chipSummary = summarizeChips(session.chips, t);

    const handleHeaderClick = () =>
      isBatch ? onToggle(session.id) : onImageClick?.(session.images[0]?.image_id);

    return (
      <div className="border-b border-border/30 last:border-b-0">
        {/* Session header */}
        <button
          type="button"
          onClick={handleHeaderClick}
          className={cn(
            "w-full flex items-start gap-2.5 px-3 py-2 text-left transition-colors",
            "hover:bg-accent/60",
          )}
        >
          {/* Thumbnails: stack for batch, single otherwise */}
          {isBatch ? (
            <div className="relative h-9 w-9 shrink-0">
              <img
                src={session.images[0]?.signed_url}
                alt=""
                className="absolute top-0 left-0 h-7 w-7 rounded object-cover bg-muted border border-background"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute bottom-0 right-0 h-5 w-5 rounded bg-muted/80 border border-background flex items-center justify-center text-[9px] font-bold text-muted-foreground">
                {session.images.length}
              </div>
            </div>
          ) : (
            <img
              src={session.images[0]?.signed_url}
              alt=""
              className="h-9 w-9 rounded object-cover shrink-0 bg-muted"
              loading="lazy"
              decoding="async"
            />
          )}

          {/* Info */}
          <div className="flex-1 min-w-0 space-y-0.5">
            {/* Who + what — summary counts chips by kind */}
            <div className="text-[11px]">
              <span className="font-semibold text-foreground">
                {session.reviewer_name}
              </span>
              <span className="text-muted-foreground">
                {chipSummary
                  ? ` ${chipSummary}`
                  : isBatch
                    ? t("recent.reviewedImages", {
                        value: formatNumber(session.images.length),
                      })
                    : t("recent.reviewed")}
              </span>
            </div>

            {/* Activity chips */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {session.chips.map((chip) => (
                <span
                  key={`${chip.kind}-${chip.family_key}-${chip.model_label ?? ""}-${chip.human_label ?? ""}`}
                  className="inline-flex items-center gap-0.5 text-[10px]"
                >
                  <ChipContent chip={chip} t={t} />
                </span>
              ))}
              {session.markedInvalid && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {t("recent.invalidFrame")}
                </span>
              )}
            </div>

            {/* Time + expand hint */}
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground font-mono">
                {formatRelativeTimeUTC(session.reviewed_at)}
              </span>
              {isBatch && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 transition-transform",
                      isExpanded && "rotate-180",
                    )}
                  />
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Expanded image list for batch sessions */}
        {isBatch && isExpanded && (
          <div className="px-3 pb-2 space-y-0.5">
            {session.images.map((img) => (
              <button
                key={img.image_id}
                type="button"
                onClick={() => onImageClick?.(img.image_id)}
                className="w-full flex items-center gap-2 pl-4 py-1 rounded hover:bg-accent/40 transition-colors text-left"
              >
                <img
                  src={img.signed_url}
                  alt=""
                  className="h-6 w-6 rounded object-cover bg-muted shrink-0"
                  loading="lazy"
                  decoding="async"
                />
                <span className="text-[10px] text-muted-foreground truncate">
                  {t("recent.imageRef", {
                    machine: img.machine_name,
                    id: img.image_id,
                  })}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);
SessionRow.displayName = "SessionRow";

const RecentReviews = memo(({ onImageClick }: RecentReviewsProps) => {
  const { t } = useTranslation("mlExports");
  const { data: activity, isLoading } = useGetReviewActivity(30);
  const [expandedSession, setExpandedSession] = useState<string | null>(null);

  const handleToggle = useCallback((sessionId: string) => {
    setExpandedSession((prev) => (prev === sessionId ? null : sessionId));
  }, []);

  const sessions = useMemo(
    () => (activity ? buildSessions(activity, t) : []),
    [activity, t],
  );

  // Summary stats
  const summary = useMemo(() => {
    if (!activity || activity.length === 0) return null;
    const reviewers = new Set(activity.map((a) => a.reviewer?.id ?? "unknown"));
    const images = new Set(activity.map((a) => a.image_id));
    return {
      corrections: images.size,
      reviewers: reviewers.size,
    };
  }, [activity]);

  // Group sessions by bucket for section headers
  const bucketedSessions = useMemo(() => {
    const buckets: { key: string; label: string; sessions: ReviewSession[] }[] = [];
    let currentBucket = "";
    for (const session of sessions) {
      if (session.bucket !== currentBucket) {
        currentBucket = session.bucket;
        buckets.push({
          key: currentBucket,
          label: BUCKET_LABEL_KEYS[currentBucket]
            ? t(BUCKET_LABEL_KEYS[currentBucket])
            : currentBucket,
          sessions: [],
        });
      }
      buckets[buckets.length - 1].sessions.push(session);
    }
    return buckets;
  }, [sessions, t]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors",
            "border border-border bg-background text-muted-foreground",
            "hover:bg-accent hover:text-foreground",
          )}
        >
          <History className="h-3.5 w-3.5" />
          {t("recent.trigger")}
          {sessions.length > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[10px] font-semibold tabular-nums">
              {formatNumber(sessions.length)}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[400px] p-0 overflow-hidden"
        align="end"
        sideOffset={8}
      >
        {/* Header with summary */}
        <div className="px-3 py-2.5 border-b bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {t("recent.heading")}
            </div>
            {summary && (
              <div className="flex items-center gap-2.5 text-[10px] text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Images className="h-3 w-3" />
                  {t("recent.imageCount", {
                    count: summary.corrections,
                    value: formatNumber(summary.corrections),
                  })}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {t("recent.reviewerCount", {
                    count: summary.reviewers,
                    value: formatNumber(summary.reviewers),
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="max-h-[380px] overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t("recent.loading")}
            </div>
          ) : sessions.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              {t("recent.empty")}
            </div>
          ) : (
            bucketedSessions.map((bucket) => (
              <div key={bucket.key}>
                {/* Time section header */}
                <div className="sticky top-0 z-10 px-3 py-1.5 bg-muted/50 backdrop-blur-sm border-b border-border/30">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {bucket.label}
                  </span>
                </div>

                {bucket.sessions.map((session) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    isExpanded={expandedSession === session.id}
                    onToggle={handleToggle}
                    onImageClick={onImageClick}
                    t={t}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});
RecentReviews.displayName = "RecentReviews";

export default RecentReviews;
